import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, IndianRupee, TrendingUp, Trophy, MessagesSquare, Phone, Footprints, CalendarCheck } from 'lucide-react';
import { toast } from 'sonner';
import adminApi from '../lib/adminApi';

// Cover: June 1 2026 → May 31 2027 (rolling next 12 months from launch)
const RANGE_START = { y: 2026, m: 6 };
const RANGE_END = { y: 2027, m: 5 };

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const monthList = () => {
  const out = [];
  let { y, m } = RANGE_START;
  while (y < RANGE_END.y || (y === RANGE_END.y && m <= RANGE_END.m)) {
    out.push({ y, m, key: `${y}-${String(m).padStart(2,'0')}`, label: `${MONTHS[m-1]} ${y}` });
    m += 1;
    if (m > 12) { m = 1; y += 1; }
  }
  return out;
};

const monthRange = (y, m) => {
  const last = new Date(y, m, 0).getDate();
  const pad = (n) => String(n).padStart(2,'0');
  return {
    start: `${y}-${pad(m)}-01`,
    end: `${y}-${pad(m)}-${pad(last)}`,
  };
};

const inr = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');

const NUM_FIELDS = [
  { key: 'sale', label: 'Sale (₹)', kind: 'money' },
  { key: 'card', label: 'Card', kind: 'money' },
  { key: 'upi', label: 'UPI', kind: 'money' },
  { key: 'cash', label: 'Cash', kind: 'money' },
  { key: 'calls', label: 'Calls', kind: 'count' },
  { key: 'messages', label: 'Msgs', kind: 'count' },
  { key: 'appt_our', label: 'Appt-Our', kind: 'count' },
  { key: 'appt_new', label: 'Appt-New', kind: 'count' },
  { key: 'walkin_our', label: 'WI-Our', kind: 'count' },
  { key: 'walkin_new', label: 'WI-New', kind: 'count' },
];

const SaleReport = () => {
  const months = useMemo(monthList, []);
  const [active, setActive] = useState(() => {
    // Default to the current month if it falls inside the range; else the last
    // month in the range.
    const now = new Date();
    const key = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    return months.find((m) => m.key === key) || months[months.length - 1];
  });
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingDate, setEditingDate] = useState(null);
  const [draft, setDraft] = useState({});
  const [monthlyHighlights, setMonthlyHighlights] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { start, end } = monthRange(active.y, active.m);
      const { data } = await adminApi.get(`/sales?start=${start}&end=${end}`);
      setRows(data.items);
      setTotals(data.totals);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load sales');
    } finally {
      setLoading(false);
    }
  }, [active]);

  useEffect(() => { load(); }, [load]);

  // Load year-wide insights once (and again after a save) so the Highlights
  // cards stay in sync with edits made in any month.
  const loadHighlights = useCallback(async () => {
    try {
      const startKey = months[0];
      const endKey = months[months.length - 1];
      const start = monthRange(startKey.y, startKey.m).start;
      const end = monthRange(endKey.y, endKey.m).end;
      const { data } = await adminApi.get(`/sales/monthly?start=${start}&end=${end}`);
      setMonthlyHighlights(data.highlights);
    } catch { /* non-fatal */ }
  }, [months]);

  useEffect(() => { loadHighlights(); }, [loadHighlights]);

  const startEdit = (row) => {
    setEditingDate(row.date);
    setDraft({ ...row });
  };

  const cancelEdit = () => {
    setEditingDate(null);
    setDraft({});
  };

  // Keyboard shortcuts on edit inputs: Enter → save, Esc → cancel.
  // Tab is native (browser walks the inputs in DOM order).
  const handleEditKey = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEdit();
    }
  };

  const saveEdit = async () => {
    try {
      const payload = NUM_FIELDS.reduce((acc, f) => {
        const v = draft[f.key];
        acc[f.key] = f.kind === 'money' ? Number(v || 0) : Number(v || 0);
        return acc;
      }, {});
      payload.notes = draft.notes || '';
      const { data } = await adminApi.put(`/sales/${editingDate}`, payload);
      setRows((prev) => prev.map((r) => (r.date === editingDate ? { ...r, ...data } : r)));
      // Refresh totals for the month
      const t = { ...totals };
      NUM_FIELDS.forEach((f) => {
        const prevRow = rows.find((r) => r.date === editingDate) || {};
        t[f.key] = (t[f.key] || 0) - (prevRow[f.key] || 0) + (data[f.key] || 0);
      });
      setTotals(t);
      loadHighlights();
      toast.success(`Saved ${editingDate}`);
      cancelEdit();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const moveMonth = (delta) => {
    const idx = months.findIndex((m) => m.key === active.key);
    const next = months[idx + delta];
    if (next) setActive(next);
  };

  return (
    <div className="admin-page">
      <header className="admin-page-header">
        <h1>Sales Report</h1>
        <p className="admin-page-sub">Daily sales, payments, calls, appointments & walk-ins — Jun 2026 to May 2027</p>
      </header>

      {/* HIGHLIGHTS */}
      {monthlyHighlights && (
        <section className="admin-section">
          <h2>Year Highlights</h2>
          <div className="admin-stats-grid">
            <HighlightCard icon={Trophy} label="Highest Sale Month" data={monthlyHighlights.top_sale_month} format="money" testid="hl-sale" />
            <HighlightCard icon={Footprints} label="Most Walk-ins Month" data={monthlyHighlights.top_walkin_month} format="count" testid="hl-walkin" />
            <HighlightCard icon={CalendarCheck} label="Most Appointments Month" data={monthlyHighlights.top_appointment_month} format="count" testid="hl-appt" />
            <HighlightCard icon={Phone} label="Most Calls Month" data={monthlyHighlights.top_calls_month} format="count" testid="hl-calls" />
            <HighlightCard icon={MessagesSquare} label="Most Messages Month" data={monthlyHighlights.top_messages_month} format="count" testid="hl-msgs" />
            <HighlightCard icon={IndianRupee} label="Most Cash Month" data={monthlyHighlights.top_cash_month} format="money" testid="hl-cash" />
          </div>
        </section>
      )}

      {/* MONTH SWITCHER */}
      <div className="admin-toolbar">
        <button onClick={() => moveMonth(-1)} className="admin-btn-secondary" disabled={active.key === months[0].key} data-testid="prev-month">
          <ChevronLeft size={14} /> Prev
        </button>
        <select value={active.key} onChange={(e) => setActive(months.find((m) => m.key === e.target.value))} data-testid="sales-month-select">
          {months.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
        </select>
        <button onClick={() => moveMonth(1)} className="admin-btn-secondary" disabled={active.key === months[months.length - 1].key} data-testid="next-month">
          Next <ChevronRight size={14} />
        </button>
        <div className="admin-toolbar-right">
          <div className="admin-month-total" data-testid="month-total-sale">
            <TrendingUp size={14} /> Month Sale: <strong>{inr(totals.sale)}</strong>
          </div>
        </div>
      </div>

      {/* TABLE */}
      <div className="admin-table-wrap">
        {loading ? (
          <div className="admin-loading">Loading…</div>
        ) : (
          <table className="admin-table sales-table" data-testid="sales-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Day</th>
                <th>Sale</th>
                <th>Card</th>
                <th>UPI</th>
                <th>Cash</th>
                <th>Calls</th>
                <th>Msgs</th>
                <th colSpan={2} className="th-grp">Appointments</th>
                <th colSpan={2} className="th-grp">Walk-ins</th>
                <th>Notes</th>
                <th className="action-col">Action</th>
              </tr>
              <tr className="th-sub">
                <th colSpan={8}></th>
                <th>Our</th><th>New</th>
                <th>Our</th><th>New</th>
                <th colSpan={2}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const isWeekend = r.weekday === 5 || r.weekday === 6;
                const editing = editingDate === r.date;
                return (
                  <tr key={r.date} className={`${isWeekend ? 'weekend-row' : ''} ${editing ? 'editing-row' : ''}`} data-testid={`sale-row-${r.date}`}>
                    <td>{r.date.slice(8)}</td>
                    <td className="day-cell">{r.day}</td>
                    {NUM_FIELDS.map((f, idx) => (
                      <td key={f.key}>
                        {editing ? (
                          <input
                            type="number"
                            min="0"
                            value={draft[f.key] ?? ''}
                            onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                            onKeyDown={handleEditKey}
                            autoFocus={idx === 0}
                            data-testid={`sale-input-${r.date}-${f.key}`}
                            className="sales-edit-input"
                          />
                        ) : (
                          f.kind === 'money' ? (r[f.key] ? inr(r[f.key]) : '—') : (r[f.key] || '—')
                        )}
                      </td>
                    ))}
                    <td className="notes-col">
                      {editing ? (
                        <input
                          type="text"
                          value={draft.notes ?? ''}
                          onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                          onKeyDown={handleEditKey}
                          className="sales-edit-input wide"
                          data-testid={`sale-input-${r.date}-notes`}
                        />
                      ) : (r.notes || '—')}
                    </td>
                    <td className="action-col">
                      {editing ? (
                        <div className="sales-edit-actions">
                          <button onClick={saveEdit} className="admin-btn-primary" data-testid={`sale-save-${r.date}`}>Save</button>
                          <button onClick={cancelEdit} className="admin-btn-secondary">×</button>
                        </div>
                      ) : (
                        <button className="admin-icon-btn edit-row-btn" onClick={() => startEdit(r)} data-testid={`sale-edit-${r.date}`}>Edit</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="totals-row">
                <td colSpan={2}><strong>Totals</strong></td>
                {NUM_FIELDS.map((f) => (
                  <td key={f.key}><strong>{f.kind === 'money' ? inr(totals[f.key]) : (totals[f.key] || 0)}</strong></td>
                ))}
                <td colSpan={2} className="action-col"></td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>
      <p className="admin-hint" data-testid="sales-keyboard-hint">
        Tip: Click <strong>Edit</strong> on any row → fill values → press <kbd>Enter</kbd> to save, <kbd>Esc</kbd> to cancel, <kbd>Tab</kbd> to move to the next column.
      </p>
    </div>
  );
};

const HighlightCard = ({ icon: Icon, label, data, format, testid }) => (
  <div className="admin-stat-card" data-testid={testid}>
    <div className="admin-stat-icon"><Icon size={22} /></div>
    <div className="admin-stat-meta">
      <div className="admin-stat-label">{label}</div>
      <div className="admin-stat-value">
        {data?.month ? formatMonth(data.month) : '—'}
      </div>
      <div className="admin-stat-sub">
        {data ? (format === 'money' ? '₹' + Number(data.value).toLocaleString('en-IN') : `${data.value} total`) : 'No data yet'}
      </div>
    </div>
  </div>
);

const formatMonth = (ym) => {
  const [y, m] = ym.split('-');
  return `${MONTHS[parseInt(m, 10) - 1]} ${y}`;
};

export default SaleReport;
